import React, { useState, useEffect, useRef } from 'react';
import AssessmentPopup from './AssessmentPopup';
import { 
  FiCheck, 
  FiSend, 
  FiTrash2, 
  FiEye,
  FiLock,
  FiUnlock,
  FiChevronDown,
  FiFileText
} from 'react-icons/fi';
import { LuCalendarClock } from 'react-icons/lu';
import testService from '../../../services/testService';
import { AssessmentEditIcon } from '../AssessmentContext';
import toast from 'react-hot-toast';
import QuestionPreviewModal from './QuestionPreviewModal';
import RoleSelectionSummary from './RoleSelectionSummary';
import { resolveAssignToSelectionNames } from './resolveAssignToSelectionNames';
import GroupsInlineSearchField from './GroupsInlineSearchField';
import { GroupsPickerMemberRow, GroupsPickerPreviewRow } from './GroupsPickerDropdownPanel';
import GroupsPickerDropdownPortal from './GroupsPickerDropdownPortal';
import GroupsMemberPreviewPopover from './GroupsMemberPreviewPopover';
import { getEffectivePendingEvalCount } from '../../../utils/assessmentEval';

const mapCollegeTemplateGroup = (g) => ({
  id: g.id || g.template_name || g.name,
  name: g.name || g.template_name || g.id,
  template_name: g.template_name || g.name,
  student_count: g.student_count ?? 0,
  student_ids: g.student_ids || [],
  students: g.students || [],
});

const buildHrStudentMap = (groups) => {
  const map = {};
  (groups || []).forEach((tpl) => {
    (tpl.students || []).forEach((s) => {
      if (s?.id != null) {
        map[String(s.id)] = s;
      }
    });
  });
  return map;
};

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  // If already contains AM/PM, return as is
  if (timeStr.includes('AM') || timeStr.includes('PM')) return timeStr;
  try {
    const [hours, minutes] = timeStr.split(':');
    let h = parseInt(hours);
    const m = minutes;
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; 
    return `${h}:${m} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const BatchRow = ({
  batch,
  assessmentId,
  onRefresh,
  onPackDeleted,
  assignedRoles,
  rolesList = [],
  assessmentCategory,
  onPreview,
  templates = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [collegeTemplates, setCollegeTemplates] = useState([]);
  const [hrStudentById, setHrStudentById] = useState({});
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [data, setData] = useState({ ...batch });
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const dropdownRef = useRef(null);
  const employeeDropdownPanelRef = useRef(null);
  const isHrFresher = assessmentCategory === 'HR Recruitment-Fresher';

  useEffect(() => {
    if (isHrFresher) {
      const fetchExternalSetupGroups = async () => {
        try {
          const res = await testService.getHrCollegeTemplates();
          if (res.status === 'success') {
            const groups = (res.college_templates || []).map(mapCollegeTemplateGroup);
            setCollegeTemplates(groups);
            setHrStudentById(buildHrStudentMap(groups));
          }
        } catch (err) {
          console.error('Failed to fetch External Setup groups:', err);
          toast.error('Failed to load External Setup list');
        }
      };
      fetchExternalSetupGroups();
      setFilteredEmployees([]);
    } else {
      setCollegeTemplates([]);
      setHrStudentById({});
      const roleSource = data.user_role || assignedRoles;
      /* LEGACY: assignedRoles from assessment.assigned_to (Create New Test).
      if (!roleSource) { setFilteredEmployees([]); return; }
      */
      if (!roleSource) {
        setFilteredEmployees([]);
        return;
      }
      const fetchEmployees = async () => {
        try {
          const res = await testService.getUsersByRoles(roleSource);
          const employees = res?.data ?? (Array.isArray(res) ? res : []);
          setFilteredEmployees(employees || []);
        } catch (err) {
          console.error('Failed to fetch filtered employees:', err);
        }
      };
      fetchEmployees();
    }
  }, [assignedRoles, data.user_role, isHrFresher]);

  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    let diff = (eH * 60 + eM) - (sH * 60 + sM);
    if (diff < 0) diff += 1440;
    return diff;
  };

  const handleTimeChange = (field, value) => {
    const newData = { ...data, [field]: value };
    const newDuration = calculateDuration(newData.start_time, newData.end_time);
    setData({ ...newData, duration: newDuration });
  };

  const buildUpdatePayload = () => {
    const userRole = Array.isArray(data.user_role)
      ? data.user_role.join(',')
      : (data.user_role ?? '');
    const candidates =
      data.candidates ??
      (Array.isArray(data.selectedCandidates) ? data.selectedCandidates.join(',') : '');

    return {
      assessment_id: assessmentId,
      pack_name: data.pack_name,
      template_id: data.template_id || null,
      user_role: userRole,
      duration: data.duration,
      scheduled_date: data.scheduled_date,
      start_time: data.start_time,
      end_time: data.end_time,
      candidates,
      candidates_type: data.candidates_type,
      status: data.status,
      instructions: data.instructions,
      proctored_exam: data.proctored_exam,
      browser_lockdown: data.browser_lockdown,
      show_results: data.show_results,
      allow_backtracking: data.allow_backtracking,
    };
  };

  const handleToggleEdit = async () => {
    if (isEditing) {
      try {
        const res = await testService.updateTestPack(data.id, buildUpdatePayload());
        if (res.status === 'success') {
          toast.success('Group updated successfully');
          setIsEditing(false);
          onRefresh?.();
        } else {
          toast.error(res.message || 'Failed to update group');
        }
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message || 'Failed to update group');
      }
    } else {
      setIsEditing(true);
      // Ensure candidates is handled as array for selection UI
      let currentCandidates = [];
      if (typeof data.candidates === 'string' && data.candidates) {
        currentCandidates = data.candidates.split(',').map(s => s.trim());
      } else if (Array.isArray(data.candidates)) {
        currentCandidates = data.candidates;
      }
      const packRoles = batch.user_role
        ? String(batch.user_role).split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      setData((prev) => ({
        ...prev,
        selectedCandidates: currentCandidates,
        user_role: packRoles.length > 0 ? packRoles : prev.user_role || [],
      }));
    }
  };

  const resolveRoleNames = (roleIds) => {
    if (!roleIds) return [];
    const ids = Array.isArray(roleIds)
      ? roleIds.map(String)
      : String(roleIds).split(',').map((s) => s.trim()).filter(Boolean);
    return ids.map((id) => {
      const found = rolesList.find((r) => String(r.id) === id);
      return found ? found.name : id;
    });
  };

  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);

  // Handle click outside for employee dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current?.contains(event.target);
      const inPanel = employeeDropdownPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsEmployeeDropdownOpen(false);
      }
    };

    if (isEmployeeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setEmployeeSearchTerm('');
      setTemplateSearchTerm('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEmployeeDropdownOpen]);

  const toggleEmployee = (empId) => {
    setData(prev => {
      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates : [];
      const updated = current.includes(String(empId))
        ? current.filter(id => id !== String(empId))
        : [...current, String(empId)];
      return { ...prev, selectedCandidates: updated, candidates: updated.join(',') };
    });
  };

  const toggleCollegeTemplate = (template) => {
    const ids = (template.student_ids || []).map(String);
    setData(prev => {
      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates.map(String) : [];
      const allSelected = ids.length > 0 && ids.every(id => current.includes(id));
      const updated = allSelected
        ? current.filter(id => !ids.includes(id))
        : [...new Set([...current, ...ids])];
      return { ...prev, selectedCandidates: updated, candidates: updated.join(',') };
    });
  };

  const isCollegeTemplateSelected = (template) => {
    const ids = (template.student_ids || []).map(String);
    const current = Array.isArray(data.selectedCandidates) ? data.selectedCandidates.map(String) : [];
    return ids.length > 0 && ids.every(id => current.includes(id));
  };

  const getVisibleCollegeTemplates = () => collegeTemplates.filter(t =>
    (t.name || '').toLowerCase().includes(templateSearchTerm.toLowerCase())
  );

  const getVisibleEmployees = () =>
    filteredEmployees.filter(
      (emp) =>
        emp.name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
        (emp.job_role && emp.job_role.toLowerCase().includes(employeeSearchTerm.toLowerCase()))
    );

  const getHrStudent = (id) => hrStudentById[String(id).trim()] || null;

  const getCandidateLabel = (id) => {
    if (isHrFresher) {
      const student = getHrStudent(id);
      return student?.student_name || `Student ID: ${id}`;
    }
    const emp = filteredEmployees.find(e => String(e.id) === String(id));
    return emp ? emp.name : `ID: ${id}`;
  };

  const getAssignedCandidateIds = () =>
    String(data.candidates || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const getSortedAssignedCandidateIds = () => {
    const ids = getAssignedCandidateIds();
    if (!isHrFresher) return ids;
    return [...ids].sort((a, b) => {
      const na = getHrStudent(a)?.student_name || '';
      const nb = getHrStudent(b)?.student_name || '';
      return na.localeCompare(nb, undefined, { sensitivity: 'base' });
    });
  };

  const handleDelete = () => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this group?',
      message: 'Are you sure you want to delete this group? This action cannot be undone.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        try {
          const res = await testService.deleteTestPack(data.id);
          setPopupConfig({ isOpen: false });
          if (res.status === 'success') {
            toast.success('Group deleted successfully');
            if (assessmentId && data.id) {
              onPackDeleted?.(assessmentId, data.id);
            } else {
              onRefresh?.();
            }
          } else {
            toast.error(res.message || 'Failed to delete group');
          }
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
          toast.error('Failed to delete group');
        }
      }
    });
  };

  const isPublished = data.status?.toLowerCase() === 'published';
  const pendingEvalCount = getEffectivePendingEvalCount(data);

  return (
    <tr className={`group transition-all ${isEditing ? 'bg-blue-50/50' : 'hover:bg-slate-50/30'} ${isPublished && !isEditing ? 'published-row' : ''}`}>
      <td className="px-6 py-5 assess-mod-groups-col-name align-middle">
        {isEditing && !isPublished ? (
          <input 
            type="text" 
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-rose-100 outline-none transition-all"
            value={data.pack_name}
            onChange={(e) => setData({...data, pack_name: e.target.value})}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <div className={`assess-mod-groups-pack-name uppercase tracking-wide ${isPublished ? 'text-[#059669]' : 'text-slate-700'}`}>{data.pack_name}</div>
            <div className={`assess-mod-groups-status inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold uppercase tracking-wide w-fit shadow-sm ${isPublished ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-slate-50 text-slate-500 border border-slate-100'}`}>
              <div className={`w-1 h-1 rounded-full ${isPublished ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
              {isPublished ? 'Published' : 'Draft'}
            </div>
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-template align-middle">
        {isEditing && !isPublished ? (
          <select 
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-rose-100 outline-none transition-all"
            value={data.template_id}
            onChange={(e) => setData({...data, template_id: e.target.value})}
          >
             <option value="">Select Template</option>
             {templates.map(t => (
               <option key={t.id} value={t.id}>{t.name}</option>
             ))}
          </select>
        ) : (
          <span className={`font-black text-xs uppercase tracking-wide ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {data.template_name || 'No Template Assigned'}
          </span>
        )}
      </td>
      {!isHrFresher && (
      <td className="px-6 py-5 assess-mod-groups-col-roles">
          <span
            className={`block truncate font-black text-xs uppercase tracking-wide ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}
            title={resolveRoleNames(data.user_role).join(', ')}
          >
            <RoleSelectionSummary names={resolveRoleNames(data.user_role)} />
          </span>
      </td>
      )}
      <td className="px-6 py-5 assess-mod-groups-col-assign align-middle">
        {isEditing && !isPublished ? (
          <div className="assess-mod-groups-picker-wrap" ref={dropdownRef}>
            <GroupsInlineSearchField
              value={isHrFresher ? templateSearchTerm : employeeSearchTerm}
              onChange={(e) => {
                if (isHrFresher) {
                  setTemplateSearchTerm(e.target.value);
                } else {
                  setEmployeeSearchTerm(e.target.value);
                }
                setIsEmployeeDropdownOpen(true);
              }}
              onFocus={async () => {
                if (isHrFresher) {
                  try {
                    const res = await testService.getHrCollegeTemplates();
                    if (res.status === 'success') {
                      const groups = (res.college_templates || []).map(mapCollegeTemplateGroup);
                      setCollegeTemplates(groups);
                      setHrStudentById(buildHrStudentMap(groups));
                    }
                  } catch (err) {
                    console.error(err);
                  }
                }
                setIsEmployeeDropdownOpen(true);
              }}
              placeholder={
                isHrFresher
                  ? 'Search groups...'
                  : !data.user_role || (Array.isArray(data.user_role) && data.user_role.length === 0)
                    ? 'Select roles first'
                    : 'Search employees...'
              }
              disabled={!isHrFresher && (!data.user_role || (Array.isArray(data.user_role) && data.user_role.length === 0))}
              dropdownOpen={isEmployeeDropdownOpen}
              onToggleDropdown={async () => {
                if (!isHrFresher && (!data.user_role || (Array.isArray(data.user_role) && data.user_role.length === 0))) {
                  toast.error('Select assigned roles first');
                  return;
                }
                if (isHrFresher) {
                  try {
                    const res = await testService.getHrCollegeTemplates();
                    if (res.status === 'success') {
                      const groups = (res.college_templates || []).map(mapCollegeTemplateGroup);
                      setCollegeTemplates(groups);
                      setHrStudentById(buildHrStudentMap(groups));
                    }
                  } catch (err) {
                    console.error(err);
                  }
                }
                setIsEmployeeDropdownOpen((open) => !open);
              }}
              selectionSummary={(() => {
                const names = resolveAssignToSelectionNames({
                  isHr: isHrFresher,
                  selectedIds: data.selectedCandidates,
                  collegeTemplates,
                  employees: filteredEmployees,
                });
                return names.length > 0 ? (
                  <RoleSelectionSummary names={names} />
                ) : null;
              })()}
              toggleAriaLabel={isHrFresher ? 'Toggle External Setup list' : 'Toggle employees list'}
            />

            <GroupsPickerDropdownPortal
              ref={employeeDropdownPanelRef}
              isOpen={isEmployeeDropdownOpen}
              anchorRef={dropdownRef}
              minWidth={256}
              title="Group Members"
              subtitle={isHrFresher ? 'External Setup' : 'Candidate List'}
              count={Array.isArray(data.selectedCandidates) ? data.selectedCandidates.length : 0}
            >
                <div className="assess-mod-groups-member-panel__list">
                  <GroupsPickerMemberRow
                    isSelectAll
                    selectAllLabel={isHrFresher ? 'Select All' : 'Select All Members'}
                    checked={isHrFresher
                      ? (() => {
                          const visible = getVisibleCollegeTemplates();
                          return visible.length > 0 && visible.every(t => isCollegeTemplateSelected(t));
                        })()
                      : (() => {
                          const visible = getVisibleEmployees();
                          return (
                            visible.length > 0 &&
                            visible.every((emp) =>
                              Array.isArray(data.selectedCandidates) &&
                              data.selectedCandidates.includes(String(emp.id))
                            )
                          );
                        })()}
                    onChange={(e) => {
                      if (isHrFresher) {
                        const visible = getVisibleCollegeTemplates();
                        const visibleIds = visible.flatMap(t => (t.student_ids || []).map(String));
                        const updated = e.target.checked ? visibleIds : [];
                        setData({ ...data, selectedCandidates: updated, candidates: updated.join(',') });
                        return;
                      }
                      const visible = getVisibleEmployees();
                      const visibleIds = visible.map((emp) => String(emp.id));
                      setData((prev) => {
                        const current = Array.isArray(prev.selectedCandidates)
                          ? prev.selectedCandidates.map(String)
                          : [];
                        const updated = e.target.checked
                          ? [...new Set([...current, ...visibleIds])]
                          : current.filter((id) => !visibleIds.includes(id));
                        return { ...prev, selectedCandidates: updated, candidates: updated.join(',') };
                      });
                    }}
                  />
                  {isHrFresher ? (
                    getVisibleCollegeTemplates().map(tpl => (
                      <GroupsPickerMemberRow
                        key={tpl.id || tpl.name}
                        name={tpl.name}
                        meta={`${tpl.student_count || 0} candidates`}
                        checked={isCollegeTemplateSelected(tpl)}
                        onChange={() => toggleCollegeTemplate(tpl)}
                      />
                    ))
                  ) : (
                    getVisibleEmployees().map(emp => (
                      <GroupsPickerMemberRow
                        key={emp.id}
                        name={emp.name}
                        meta={emp.job_role}
                        checked={Array.isArray(data.selectedCandidates) && data.selectedCandidates.includes(String(emp.id))}
                        onChange={() => toggleEmployee(emp.id)}
                      />
                    ))
                  )}
                </div>
            </GroupsPickerDropdownPortal>
          </div>
        ) : (
          <div className="assess-mod-groups-meta font-semibold text-slate-600 flex flex-col gap-0.5">
            {data.candidates ? (
              <>
                <GroupsMemberPreviewPopover
                  badgeCount={getAssignedCandidateIds().length}
                  subtitle={isHrFresher ? 'External Setup' : 'Candidate List'}
                  trigger={
                    <div className={`flex items-center gap-1.5 font-black uppercase tracking-tighter cursor-help ${isPublished ? 'text-[#b91c1c]' : 'text-red-500'}`}>
                      <i className="bi bi-people-fill text-xs"></i>
                      <span>{String(data.candidates).split(',').length} Candidates</span>
                    </div>
                  }
                >
                  {getSortedAssignedCandidateIds().map((id) => {
                    const hrStudent = isHrFresher ? getHrStudent(id) : null;
                    const label = getCandidateLabel(id);
                    return isHrFresher ? (
                      <GroupsPickerPreviewRow
                        key={id}
                        name={hrStudent?.student_name || `Student #${id}`}
                        meta={hrStudent?.register_number || '—'}
                      />
                    ) : (
                      <GroupsPickerPreviewRow key={id} name={label} />
                    );
                  })}
                </GroupsMemberPreviewPopover>
              </>
            ) : (
              <span className="text-slate-300">No Assignment</span>
            )}
            <div className="mt-1 flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
              <div className="flex items-center gap-1 text-emerald-600">
                <i className="bi bi-check-all text-xs"></i>
                <span className="font-black uppercase tracking-widest text-xs leading-none whitespace-nowrap">{data.completed_count || 0} COMPLETED</span>
              </div>
              {pendingEvalCount > 0 && (
                <div className="flex items-center gap-1 text-red-500">
                  <i className="bi bi-clock-history text-xs"></i>
                  <span className="font-black uppercase tracking-widest text-xs leading-none whitespace-nowrap">{pendingEvalCount} PENDING EVAL</span>
                </div>
              )}
            </div>
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-date align-middle">
        {isEditing ? (
          <input
            type="date"
            className="assess-mod-groups-datetime-input w-full bg-white outline-none transition-all focus:ring-2 focus:ring-rose-100"
            value={data.scheduled_date}
            onChange={(e) => setData({ ...data, scheduled_date: e.target.value })}
          />
        ) : (
          <div className={`font-black text-xs tracking-wider whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {data.scheduled_date || '—'}
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-start align-middle">
        {isEditing ? (
          <input
            type="time"
            className="assess-mod-groups-datetime-input w-full bg-white outline-none transition-all focus:ring-2 focus:ring-rose-100"
            value={data.start_time}
            onChange={(e) => handleTimeChange('start_time', e.target.value)}
          />
        ) : (
          <div className={`font-black text-xs tracking-wider uppercase whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {formatTime(data.start_time) || '—'}
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-end align-middle">
        {isEditing ? (
          <input
            type="time"
            className="assess-mod-groups-datetime-input w-full bg-white outline-none transition-all focus:ring-2 focus:ring-rose-100"
            value={data.end_time}
            onChange={(e) => handleTimeChange('end_time', e.target.value)}
          />
        ) : (
          <div className={`font-black text-xs tracking-wider uppercase whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {formatTime(data.end_time) || '—'}
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-duration align-middle">
        <div className="assess-mod-groups-duration-wrap">
          <span className={`font-black text-sm whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {data.duration ?? '—'}
          </span>
          <span className="text-slate-400 font-black text-xs uppercase">M</span>
        </div>
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-action align-middle text-right">
        <div className="assess-mod-groups-action-wrap">
          {isPublished ? (
            <>
              <button 
                onClick={() => setIsPreviewOpen(true)}
                className="assess-mod-groups-action-btn bg-slate-50 text-slate-500 hover:bg-slate-500 hover:text-white border border-slate-100 group"
                title="Preview Question Paper"
              >
                <FiEye className="group-hover:scale-110 transition-transform" />
              </button>

              <button 
                onClick={handleToggleEdit}
                className={`assess-mod-groups-action-btn group ${isEditing ? 'bg-emerald-600 text-white' : 'bg-orange-50 text-orange-500 hover:bg-orange-100 border border-orange-100'}`}
                title={isEditing ? "Save Schedule" : "Reschedule Group"}
              >
                {isEditing ? <FiCheck className="group-hover:scale-110 transition-transform" /> : <LuCalendarClock className="group-hover:scale-110 transition-transform" />}
              </button>

              <button 
                onClick={handleDelete}
                className="assess-mod-groups-action-btn bg-red-50 text-red-500 hover:bg-[#b91c1c] hover:text-white border border-red-100 group" 
                title="Delete Group"
              >
                <FiTrash2 className="group-hover:scale-110 transition-transform" />
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={handleToggleEdit}
                className={`assess-mod-groups-action-btn group ${isEditing ? 'bg-emerald-500 text-white shadow-emerald-100' : 'bg-[#f0fdf4] text-[#10b981] hover:bg-[#d1fae5]'}`}
                title={isEditing ? "Save Changes" : "Edit Group"}
              >
                {isEditing ? <FiCheck className="group-hover:scale-110 transition-transform" /> : <AssessmentEditIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />}
              </button>
              
              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsPreviewOpen(true)}
                    className="assess-mod-groups-action-btn bg-indigo-50 text-indigo-500 hover:bg-indigo-500 hover:text-white group"
                    title="Preview Question Paper"
                  >
                    <FiEye className="group-hover:scale-110 transition-transform" />
                  </button>
                  <button 
                    onClick={() => {
                      setPopupConfig({
                        isOpen: true,
                        type: 'warning',
                        title: 'Publish this group?',
                        message: 'This will make the test visible to assigned candidates.',
                        confirmText: 'Yes, publish it!',
                        onConfirm: async () => {
                          const res = await testService.publishTestPack(data.id);
                          if (res.status === 'success') {
                            setPopupConfig({
                              isOpen: true,
                              type: 'warning',
                              title: 'Published!',
                              message: 'The group is now live.',
                              showCancel: false,
                              confirmText: 'OK',
                              onConfirm: () => {
                                setPopupConfig({ isOpen: false });
                                onRefresh?.();
                              }
                            });
                          } else {
                            toast.error(res.message || 'Failed to publish group');
                            setPopupConfig({ isOpen: false });
                          }
                        }
                      });
                    }}
                    className="assess-mod-groups-action-btn bg-[#fff7ed] text-[#f97316] hover:bg-[#ffedd5] group" 
                    title="Publish Group"
                  >
                    <FiSend className="group-hover:scale-110 transition-transform" />
                  </button>
                  
                  <button 
                    onClick={handleDelete}
                    className="assess-mod-groups-action-btn bg-[#fff1f2] text-[#e11d48] hover:bg-[#ffe4e6] group" 
                    title="Delete Group"
                  >
                    <FiTrash2 className="group-hover:scale-110 transition-transform" />
                  </button>
                </>
              )}
            </>
          )}
        </div>

        <QuestionPreviewModal 
          isOpen={isPreviewOpen} 
          onClose={() => setIsPreviewOpen(false)} 
          packId={data.id}
          batchName={data.pack_name}
          packSnapshot={data}
        />
      </td>
      <AssessmentPopup 
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
    </tr>
  );
};

export default BatchRow;
